import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { showAlert } from '../../appToast/appToastSlice';
import { IAccountLogo } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ICreateAccountLogoResponse {
  status?: boolean;
  message?: string;
  data?: IAccountLogo;
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateAccountLogoResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createAccountLogoAsync = createAsyncThunk(
  'savings/accountLogos/create',
  async (formData: FormData, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ICreateAccountLogoResponse>(
        '/savings/account-logos',
        formData,
        getSavingsAuthConfig(state, {
          'Content-Type': 'multipart/form-data',
        }),
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Logo uploaded successfully',
          type: 'drawer',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateAccountLogoResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: getApiErrorMessage(error.response?.data),
          type: 'drawer',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const accountLogoCreateSlice = createSlice({
  name: 'savings/accountLogos/create',
  initialState,
  reducers: {
    resetCreateAccountLogo(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAccountLogoAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createAccountLogoAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateAccountLogoResponse;
      })
      .addCase(createAccountLogoAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateAccountLogoResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetCreateAccountLogo } = accountLogoCreateSlice.actions;

export default accountLogoCreateSlice.reducer;
